"use client";

import {
  Y,
  X,
  Button,
  Input,
  Modal,
  Textarea,
  Text,
  Select,
} from "@reframe.so/blocks";
import { Statues, TicketSort, TicketStatus, TicketView } from "./constants";
import { TTicket, createTicket, upvoteTicket } from "./server";
import { SignInButtons } from "@src/app/(auth)/sign-in/client";
import { useMutation } from "@src/hooks/mutation";
import { useSearch } from "@src/hooks/search";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { LayoutGridIcon } from "@reframe.so/icons/icons/LayoutGridIcon";
import { AlignJustifyIcon } from "@reframe.so/icons/icons/AlignJustifyIcon";

export const CreateTicket = () => {
  const session = useSession();
  const isSignedIn = session.status === "authenticated";
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const router = useRouter();

  const [loading, handleCreateTicket] = useMutation(async () => {
    const ticket = await createTicket({
      title,
      description,
    });
    setTitle("");
    setDescription("");
    router.push(`/roadmap/${ticket.id}`);
    setOpen(false);
  });

  const createTicketModal = (
    <>
      <Y css="gap-7">
        <Y css="gap-2">
          <Input
            placeholder="Name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </Y>
        <Y css="gap-2">
          <Textarea
            css=" max-h-48"
            placeholder="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </Y>
      </Y>

      <Button
        size="lg"
        onClick={handleCreateTicket}
        disabled={title === ""}
        loading={loading}
      >
        Request Feature
      </Button>
    </>
  );
  const signinModal = (
    <Y>
      <SignInButtons next={"roadmap"} />
    </Y>
  );

  return (
    <>
      <Button css="shrink-0" onClick={() => setOpen(true)}>
        + <Text css="ml-2 hidden sm:flex">Request Feature</Text>
      </Button>

      <Modal
        open={open}
        setOpen={setOpen}
        title={
          isSignedIn
            ? "Request a new feature"
            : "Sign in to request a new feature"
        }
        css="gap-6 justify-center"
      >
        {isSignedIn ? createTicketModal : signinModal}
      </Modal>
    </>
  );
};

export const Roadmap = ({ children }: { children: React.ReactNode }) => {
  const { data: session } = useSession();
  const tabs = Object.entries(Statues).filter(
    ([tab]) => tab !== TicketStatus.archived || session?.user?.admin
  );

  const [search, setSearch] = useSearch<{
    status?: TicketStatus;
    sort?: TicketSort;
    view?: TicketView;
  }>();

  return (
    <X css="grow hug justify-center min-h-0">
      <Y
        css={[
          "grow w-full p-6",
          search.view === "list" ? "items-center" : "items-start",
        ]}
      >
        <Y
          css={[
            "p-4 justify-between items-center gap-4 sm:flex-row w-full",
            search.view === "list" ? "max-w-5xl" : "max-w-[calc(100%-24px)]",
          ]}
        >
          <X css="gap-2 w-full wrap">
            <X>
              <Select
                value={search.status ?? "all"}
                onChange={(status) => {
                  setSearch({
                    status: status === "all" ? null : (status as TicketStatus),
                  });
                }}
              >
                <Select.Option value="all">🙋‍♀️ All</Select.Option>
                {tabs.map(([tab, { name, emoji }]) => (
                  <Select.Option
                    key={tab}
                    value={tab}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {emoji} {name}
                  </Select.Option>
                ))}
              </Select>
            </X>
            <X>
              <Select
                value={search.sort ?? "popular"}
                onChange={(sort) => {
                  setSearch({
                    sort: sort as TicketSort,
                  });
                }}
              >
                <Select.Option value={TicketSort.newest}>
                  🍓 Newest
                </Select.Option>
                <Select.Option value={TicketSort.popular}>
                  🤳 Popular
                </Select.Option>
                <Select.Option value={TicketSort.recentComment}>
                  💬 Recent comments
                </Select.Option>
              </Select>
            </X>
            <X css="items-center grow justify-between gap-1">
              <X>
                <X
                  css={[
                    "p-2 rounded-l-md shadow-md border cursor-pointer",
                    search.view === "list" ? "bg-gray-200" : "",
                  ]}
                  onClick={() => setSearch({ view: "list" as TicketView })}
                >
                  <AlignJustifyIcon css="h-5 w-5 " />
                </X>

                <X
                  css={[
                    "p-2 rounded-r-md shadow-md border cursor-pointer",
                    search.view === "list" ? "" : "bg-gray-200",
                  ]}
                  onClick={() => setSearch({ view: "board" as TicketView })}
                >
                  <LayoutGridIcon css="h-5 w-5" />
                </X>
              </X>
              <CreateTicket />
            </X>
          </X>
        </Y>

        {children}
      </Y>
    </X>
  );
};

export const Ticket = ({ ticket }: { ticket: TTicket }) => {
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const session = useSession();
  const isSignedIn = session.status === "authenticated";
  const [upvoting, upvote] = useMutation(async (id: number) => {
    await upvoteTicket(id);
  });
  const [{ view }] = useSearch<{
    view?: TicketView;
  }>();

  return (
    <Y
      css={[
        "justify-between border border-solid border-primary-50 rounded-md gap-3 p-5 shadow-sm  min-h-[205px] border-b-primary-400/30 hover:border-primary-400/70 border-b-1 hover:shadow-md hover:border-2 bg-white min-w-[315px]",
        view === "list" ? "grow basis-full sm:basis-5/12" : "",
      ]}
    >
      <Y css="gap-6">
        <X css="items-center gap-2 text-neutral-800">
          <Link href={`/roadmap/${ticket.id}`}>
            <Text css="capitalize text-md line-clamp-1 max-w-[40ch] text-primary-400">
              {Statues[ticket.status as TicketStatus]!.emoji} {ticket.title}
            </Text>
          </Link>
        </X>

        <Text css="text-sm text-neutral-500 line-clamp-3 break-words [word-break:break-word]">
          {ticket.description}
        </Text>
      </Y>

      <X css="items-center justify-between text-neutral-500">
        <X css="gap-2 items-center flex-nowrap  ">
          <Button
            variant="secondary"
            onClick={() => {
              if (!isSignedIn) {
                setOpen(true);

                return;
              }
              upvote(ticket.id);
            }}
            css={[
              "shrink-0 rounded-full hover:outline-1 p-4 text-neutral-600 hover:bg-primary-100 hover:text-primary-900",
              ticket.votes.length === 0
                ? "bg-primary-50/60"
                : "bg-emerald-300/60 hover:bg-emerald-300/60",
            ]}
          >
            {ticket.voteCount === 0
              ? "Upvote"
              : `${ticket.voteCount} upvote${
                  ticket.voteCount === 1 ? "" : "s"
                }`}{" "}
            🙌
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              router.push(`/roadmap/${ticket.id}`);
            }}
            css=" bg-primary-50 shrink-0 rounded-full hover:outline-1 hover:outline-primary-400 p-4 text-neutral-600 hover:bg-primary-100 hover:text-primary-900"
          >
            {ticket.commentCount === 0
              ? "Comment"
              : `${ticket.commentCount} comment${
                  ticket.commentCount === 1 ? "" : "s"
                }`}{" "}
            🤔
          </Button>
        </X>
      </X>
      <Modal
        open={open}
        setOpen={setOpen}
        title={"Please sign in to vote"}
        css="gap-6 justify-center"
      >
        <Y>
          <SignInButtons next={"roadmap"} />
        </Y>
      </Modal>
    </Y>
  );
};
